// Rebuild the Fees form's data from a stored quote, so a summary PDF can be
// regenerated for a transaction whose PDF never reached S3 (older web quotes,
// and every quote made through the DMV Nav API, which never uploads one).
//
// The quote's "Inputs" carry the values that were sent to ATC - the same codes
// redux-state/fees/api.js (submitFee) builds from the form - so this maps them
// back onto the form's own field names, which is what generateFeesReport reads.

import { REFINANCE } from './transactionType';

const TRANSACTION_TYPES = { P: 'Purchase', RE: REFINANCE };
const PLATE_TYPES = { R: 'Regular', C: 'Commercial' };

const isYes = (value) => ['1', 'true', 'yes', 'y'].includes(String(value ?? '').trim().toLowerCase());

const pick = (inputs, ...keys) => {
  for (const key of keys) {
    const value = inputs?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
};

export const feesFormDataFromRecord = (inputs = {}, transactionId = '') => {
  const rawType = String(pick(inputs, 'TransactionType', 'transactionType'));
  // The transaction ID's suffix is authoritative: _REF is always a refi quote.
  const transactionType = String(transactionId).endsWith('_REF')
    ? REFINANCE
    : TRANSACTION_TYPES[rawType] || rawType || 'Purchase';
  const rawPlate = String(pick(inputs, 'PlateType', 'plateType'));

  return {
    zipCode: pick(inputs, 'ZIP', 'zip'),
    city: pick(inputs, 'City', 'city'),
    country: pick(inputs, 'County', 'county'),
    state: pick(inputs, 'State', 'state'),
    outSideCityLimit: isYes(pick(inputs, 'OutsideCityLimits')),
    vinCode: pick(inputs, 'VIN', 'vinNumber'),
    modelYear: pick(inputs, 'ModelYear', 'modelYear'),
    make: pick(inputs, 'Make', 'make'),
    model: pick(inputs, 'Model', 'model'),
    fuelType: pick(inputs, 'FuelType', 'fuelType'),
    msrp: pick(inputs, 'MSRP', 'msrp'),
    // ATC code (PV, LT, SUV...); generateFeesReport labels it via vehicleTypeLabel.
    vehicleType: pick(inputs, 'VehicleType', 'vehicleType'),
    vehicleWeight: pick(inputs, 'Weight'),
    gvw: pick(inputs, 'GVW', 'gvw'),
    gvwr: pick(inputs, 'GVWR', 'gvwr'),
    cityMpg: pick(inputs, 'MPG'),
    highwayMpg: pick(inputs, 'HighwayMPG'),
    combinedMpg: pick(inputs, 'CombinedMPG'),
    horsepower: pick(inputs, 'HP', 'hp'),
    cylinders: pick(inputs, 'Cylinders', 'cylinders'),
    transactionType,
    plateTransfer: isYes(pick(inputs, 'IsTransfer')),
    plateType: PLATE_TYPES[rawPlate] || rawPlate,
    titleStatus: pick(inputs, 'TitleStatus', 'titleStatus'),
    registrationMonths: pick(inputs, 'RegistrationMonths', 'registrationMonths'),
    military: String(pick(inputs, 'RegistrationOptions')).toUpperCase() === 'M',
    basePrice: pick(inputs, 'BasePrice', 'basePrice'),
    docsFees: pick(inputs, 'DocFees', 'docsFees'),
    extendedWarranties: pick(inputs, 'ExtendedWarranties', 'ExtendedWarrantiesField'),
    Additions: pick(inputs, 'Additions'),
    ProtectionPackageField: pick(inputs, 'ProtectionPackages', 'ProtectionPackageField'),
    TheftProtectionField: pick(inputs, 'TheftProtections', 'TheftProtectionField'),
    InsurancePackages: pick(inputs, 'InsurancePackages'),
    DeliveryFees: pick(inputs, 'DeliveryFees'),
    TradeInValue: pick(inputs, 'TradeInValue'),
    TradeInYear: pick(inputs, 'TradeInYear'),
    ManufacturerRebate: pick(inputs, 'ManufacturerRebate'),
    DealerServiceFees: pick(inputs, 'DealerServiceFees'),
    DealerInventoryTax: pick(inputs, 'DealerInventoryTax'),
  };
};
